import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { AREAS, CLASSIFICACOES, STATUS, labelOf } from "@/lib/constants";
import { Search, LayoutGrid, List, Download, ExternalLink } from "lucide-react";
import SeedDetailModal from "@/components/SeedDetailModal";
import ModuleHelp from "@/components/help/ModuleHelp";

export default function Biblioteca() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    q: "", status: "", area: "", classificacao: "", pais: "", horizonte: "",
    inserido_por: "", revisado_por: "",
  });
  const [view, setView] = useState("table");
  const [loading, setLoading] = useState(false);
  const [openSeed, setOpenSeed] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    const { data } = await api.get("/seeds", { params });
    setItems(data);
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Carregar usuários para filtros (admin/coord); falha silenciosa para pesquisador
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([]));
  }, []);

  const userOptions = useMemo(() => users.filter((u) => u.name).sort((a, b) => a.name.localeCompare(b.name)), [users]);

  // Países distintos das sementes atuais (para filtro dinâmico)
  const countries = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.source_pais && s.add(i.source_pais));
    return Array.from(s).sort();
  }, [items]);

  // Horizontes distintos
  const horizons = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.horizonte_temporal && s.add(i.horizonte_temporal));
    return Array.from(s).sort();
  }, [items]);

  const statusBadge = (s) => {
    const def = STATUS.find((x) => x.value === s);
    return <span className={`badge ${def?.className || "badge-muted"}`}>{def?.label || s}</span>;
  };

  return (
    <div className="space-y-4" data-testid="biblioteca-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mil text-[var(--gold)]">Acervo Prospectivo</div>
          <h1 className="font-display text-3xl font-bold mt-1 flex items-center gap-3">Biblioteca de Fontes <ModuleHelp module="biblioteca" /></h1>
          <p className="text-[var(--text-muted)] text-sm mt-2">
            {items.length} sementes catalogadas · clique em uma linha para abrir o detalhe completo.
          </p>
        </div>
        <div className="flex gap-2">
          <a href={`${API_BASE}/export/seeds.csv`} className="btn-ghost text-xs flex items-center gap-2" data-testid="biblioteca-export-csv">
            <Download size={12} /> Exportar CSV
          </a>
          <button onClick={() => setView(view === "table" ? "cards" : "table")} className="btn-ghost text-xs flex items-center gap-2" data-testid="biblioteca-toggle-view">
            {view === "table" ? <LayoutGrid size={12} /> : <List size={12} />}
            {view === "table" ? "Cards" : "Tabela"}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card-mil p-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="biblioteca-filters">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input className="input-mil pl-9" placeholder="Buscar por título, palavra-chave..." value={filters.q}
                 onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="biblioteca-search" />
        </div>
        <select className="input-mil" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="filter-status">
          <option value="">Todos status</option>
          {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select className="input-mil" value={filters.area} onChange={(e) => setFilters({ ...filters, area: e.target.value })} data-testid="filter-area">
          <option value="">Todas áreas</option>
          {AREAS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <select className="input-mil" value={filters.classificacao} onChange={(e) => setFilters({ ...filters, classificacao: e.target.value })} data-testid="filter-classificacao">
          <option value="">Todas classificações</option>
          {CLASSIFICACOES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <select className="input-mil" value={filters.pais} onChange={(e) => setFilters({ ...filters, pais: e.target.value })} data-testid="filter-pais">
          <option value="">Todos países</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input-mil" value={filters.horizonte} onChange={(e) => setFilters({ ...filters, horizonte: e.target.value })} data-testid="filter-horizonte">
          <option value="">Todos horizontes</option>
          {horizons.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <select className="input-mil" value={filters.inserido_por} onChange={(e) => setFilters({ ...filters, inserido_por: e.target.value })} data-testid="filter-inserido-por">
          <option value="">Inserido por (qualquer)</option>
          {userOptions.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
        <select className="input-mil" value={filters.revisado_por} onChange={(e) => setFilters({ ...filters, revisado_por: e.target.value })} data-testid="filter-revisado-por">
          <option value="">Revisado por (qualquer)</option>
          {userOptions.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
      </div>

      {loading && <div className="font-mono text-xs text-[var(--text-muted)]">CARREGANDO…</div>}

      {view === "table" ? (
        <div className="card-mil overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm" data-testid="biblioteca-table">
              <thead>
                <tr className="text-left">
                  {["Título da Semente", "Fonte", "Área", "Classif.", "PESTEL", "País", "Horizonte", "Status", "Inserido por", "Revisado por", ""].map((h) => (
                    <th key={h} className="px-4 py-3 label-mil border-b border-[var(--border)] whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr
                    key={s.seed_id}
                    className="border-b border-[var(--border)]/50 hover:bg-[var(--surface-2)] cursor-pointer"
                    onClick={() => setOpenSeed(s.seed_id)}
                    data-testid={`row-${s.seed_id}`}
                  >
                    <td className="px-4 py-3 max-w-[240px]">
                      <div className="flex items-center gap-2">
                        <div className="font-semibold truncate">{s.titulo_insumo}</div>
                        {s.is_demo && <span className="badge badge-muted shrink-0" title="Registro de demonstração">DEMO</span>}
                      </div>
                      <div className="text-[10px] text-[var(--text-muted)] font-mono truncate">{s.seed_id}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[200px] text-[var(--text-muted)] text-xs truncate">{s.source_titulo}</td>
                    <td className="px-4 py-3 text-xs">{labelOf(AREAS, s.area_tematica_principal)}</td>
                    <td className="px-4 py-3"><span className="badge badge-gold">{s.classificacao_semente}</span></td>
                    <td className="px-4 py-3 text-[10px] font-mono whitespace-nowrap">{(s.perspectiva_pestel || []).join("·")}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{s.source_pais || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">{s.horizonte_temporal || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{statusBadge(s.status_validacao)}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap max-w-[160px] truncate">
                      {s.created_by_user_name || s.pesquisador_nome || <span className="text-[var(--text-muted)]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap max-w-[160px] truncate">
                      {s.reviewed_by_user_name
                        ? <span>{s.reviewed_by_user_name}</span>
                        : <span className="text-[var(--text-muted)] italic">Não revisado</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.source_link && (
                        <a href={s.source_link} target="_blank" rel="noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           className="text-[var(--info)] hover:text-[var(--gold)]">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr><td colSpan={11} className="text-center p-8 text-[var(--text-muted)] text-sm">Nenhum registro encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <button
              key={s.seed_id}
              type="button"
              className="card-mil p-5 gold-accent-top text-left hover:border-[var(--gold)]/40 transition-colors"
              onClick={() => setOpenSeed(s.seed_id)}
              data-testid={`card-${s.seed_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <span className="badge badge-gold">{s.classificacao_semente}</span>
                {statusBadge(s.status_validacao)}
              </div>
              <h3 className="font-display font-bold text-sm leading-tight mb-2 line-clamp-3">{s.titulo_insumo}</h3>
              <p className="text-xs text-[var(--text-muted)] line-clamp-3 mb-3">{s.resumo_executivo || s.citacao_direta || "—"}</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {(s.perspectiva_pestel || []).map((p) => <span key={p} className="badge badge-info">{p}</span>)}
              </div>
              <div className="flex items-center justify-between text-[10px] font-mono text-[var(--text-muted)]">
                <span className="truncate">{s.created_by_user_name || "—"}</span>
                <span>{s.horizonte_temporal}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <SeedDetailModal seedId={openSeed} onClose={() => setOpenSeed(null)} onChanged={load} />
    </div>
  );
}
